/* Poke-Geek V2 — Product page enhancements */

(() => {
  const localeCode = () =>
    (document.documentElement.lang || 'fr').toLowerCase().split('-')[0];

  const copyByLocale = {
    fr: {
      shipping: 'Expédition suivie et protégée depuis la France',
      returns: 'Retours et remboursements',
      recommendations: 'Vous aimerez aussi',
    },
    en: {
      shipping: 'Tracked and protected shipping from France',
      returns: 'Returns and refunds',
      recommendations: 'You may also like',
    },
    es: {
      shipping: 'Envío con seguimiento y protegido desde Francia',
      returns: 'Devoluciones y reembolsos',
      recommendations: 'También te puede gustar',
    },
    de: {
      shipping: 'Sendungsverfolgter und geschützter Versand aus Frankreich',
      returns: 'Rückgaben und Erstattungen',
      recommendations: 'Das könnte dir auch gefallen',
    },
    it: {
      shipping: 'Spedizione tracciata e protetta dalla Francia',
      returns: 'Resi e rimborsi',
      recommendations: 'Potrebbe piacerti anche',
    },
    nl: {
      shipping: 'Gevolgde en beschermde verzending vanuit Frankrijk',
      returns: 'Retouren en terugbetalingen',
      recommendations: 'Dit vind je misschien ook leuk',
    },
  };

  const getLocaleCopy = () => copyByLocale[localeCode()] || copyByLocale.fr;

  const replacementKeys = new Map([
    ['Reliable shipping', 'shipping'],
    ['Flexible returns', 'returns'],
    ['You may also like', 'recommendations'],
    ['Expédition suivie et protégée depuis la France', 'shipping'],
    ['Retours et remboursements', 'returns'],
    ['Vous aimerez aussi', 'recommendations'],
  ]);

  const preorderPattern = /^(?:⚠️?\s*)?(?:Précommande|Pre[- ]?order|Preventa|Reserva|Vorbestellung|Preordine|Voorbestelling)\s*:/i;
  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const getProductSections = (root = document) => {
    if (root instanceof Element && root.matches('.product-information')) {
      return [root];
    }

    return Array.from(root.querySelectorAll?.('.product-information') || []);
  };

  const findDescriptionBlock = (detailsContent) => {
    const children = Array.from(detailsContent?.children || []);
    const candidate = children.at(-1);

    if (!(candidate instanceof Element)) return null;
    if (!candidate.matches('.text-block')) return null;
    if (normalize(candidate.textContent).length === 0) return null;

    return candidate;
  };

  const getMeaningfulElementChildren = (element) =>
    Array.from(element?.children || []).filter((child) => {
      if (!(child instanceof Element)) return false;
      if (child.hidden || child.getAttribute('aria-hidden') === 'true') return false;
      if (normalize(child.textContent).length > 0) return true;
      return Boolean(child.querySelector('img, video, iframe, table'));
    });

  const findDescriptionFlowRoot = (descriptionBlock) => {
    let current = descriptionBlock;

    for (let depth = 0; depth < 5; depth += 1) {
      const children = getMeaningfulElementChildren(current);

      if (children.length >= 2) return current;
      if (children.length !== 1) return null;

      current = children[0];
    }

    return null;
  };

  const structureProductInformationFlow = (shell, descriptionBlock) => {
    if (!shell || shell.dataset.pgDescriptionFlow === 'true') return;

    const layout = shell.querySelector('.pg-pdp-long-content__layout');
    const specs = layout?.querySelector(':scope > .pg-pdp-specs');
    if (!layout || !specs) return;

    const flowRoot = findDescriptionFlowRoot(descriptionBlock);
    if (!flowRoot) return;

    const items = getMeaningfulElementChildren(flowRoot);
    if (items.length < 2) return;

    const lead = items[0];

    flowRoot.classList.add('pg-pdp-description-flow');
    lead.classList.add('pg-pdp-description-lead');
    specs.classList.add('pg-pdp-specs--in-flow');

    lead.insertAdjacentElement('afterend', specs);

    items.slice(1).forEach((item) => {
      item.classList.add('pg-pdp-description-detail');
    });

    layout.classList.add('pg-pdp-long-content__layout--flow');
    shell.dataset.pgDescriptionFlow = 'true';
  };

  const rebalanceProductInformationFlow = (shell, descriptionBlock) => {
    if (!shell || shell.dataset.pgDescriptionBalance === 'true') return;

    const flowRoot = findDescriptionFlowRoot(descriptionBlock);
    const specs = flowRoot?.querySelector(':scope > .pg-pdp-specs--in-flow');
    const lead = flowRoot?.querySelector(':scope > .pg-pdp-description-lead');

    if (!flowRoot || !specs || !lead) return;

    const section = shell.closest('.product-information');
    const hasTopPreorderNote = Boolean(section?.querySelector('.pg-pdp-preorder-note'));
    const candidates = Array.from(flowRoot.children).filter(
      (item) =>
        item instanceof Element &&
        item !== lead &&
        item !== specs &&
        !item.hidden &&
        item.getAttribute('aria-hidden') !== 'true'
    );

    const leadColumn = document.createElement('div');
    leadColumn.className = 'pg-pdp-description-lead-column';
    leadColumn.style.display = 'flex';
    leadColumn.style.flexDirection = 'column';
    leadColumn.style.gap = 'clamp(1rem, 1.6vw, 1.35rem)';
    leadColumn.style.minWidth = '0';
    leadColumn.style.gridColumn = '1';

    lead.insertAdjacentElement('beforebegin', leadColumn);
    lead.style.margin = '0';
    leadColumn.append(lead);

    let supportCount = 0;

    candidates.forEach((item) => {
      const text = normalize(item.textContent);

      if (hasTopPreorderNote && preorderPattern.test(text)) {
        item.hidden = true;
        item.setAttribute('aria-hidden', 'true');
        item.classList.add('pg-pdp-description-duplicate-preorder');
        return;
      }

      const isSupportCandidate =
        supportCount < 2 &&
        text.length >= 40 &&
        text.length <= 900 &&
        !item.querySelector('table, ul, ol, img, video, iframe');

      if (!isSupportCandidate) return;

      item.classList.remove('pg-pdp-description-detail');
      item.classList.add('pg-pdp-description-lead-support');
      item.style.margin = '0';
      leadColumn.append(item);
      supportCount += 1;
    });

    leadColumn.insertAdjacentElement('afterend', specs);
    shell.dataset.pgDescriptionBalance = 'true';
  };

  const getPreorderSummary = (descriptionBlock) => {
    const candidates = Array.from(descriptionBlock?.querySelectorAll('div, p') || []);
    const source = candidates.find((element) => {
      const text = normalize(element.textContent);
      if (!preorderPattern.test(text)) return false;

      return !Array.from(element.querySelectorAll(':scope > div, :scope > p')).some((child) =>
        preorderPattern.test(normalize(child.textContent))
      );
    });

    if (!source) return '';

    const cleaned = normalize(source.textContent).replace(preorderPattern, '');
    const sentences = cleaned.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [];

    return sentences.slice(0, 2).join(' ').trim();
  };

  const getLegacyPreorderElements = (descriptionBlock) =>
    Array.from(descriptionBlock?.querySelectorAll('div, p') || []).filter((element) => {
      const text = normalize(element.textContent);
      if (!preorderPattern.test(text)) return false;

      return !Array.from(element.querySelectorAll(':scope > div, :scope > p')).some((child) =>
        preorderPattern.test(normalize(child.textContent))
      );
    });

  const getPreorderContext = (section, descriptionBlock) => {
    const productForm = section?.querySelector('product-form-component[data-pg-preorder]');
    const hasExplicitState = productForm?.dataset.pgPreorderExplicit === 'true';
    const isPreorder = productForm?.dataset.pgPreorder === 'true';
    // TEMPORARY FALLBACK — read the historical description only while the
    // product-level boolean is absent.
    const summary = hasExplicitState
      ? isPreorder
        ? normalize(productForm?.dataset.pgPreorderDateLabel)
        : ''
      : getPreorderSummary(descriptionBlock);

    return {
      productForm,
      hasExplicitState,
      isPreorder,
      summary,
      label: normalize(productForm?.dataset.pgPreorderLabel),
    };
  };

  const syncLegacyPreorderText = (descriptionBlock, context) => {
    descriptionBlock
      ?.querySelectorAll('.pg-pdp-description-duplicate-preorder')
      .forEach((element) => {
        element.hidden = false;
        element.removeAttribute('aria-hidden');
        element.classList.remove('pg-pdp-description-duplicate-preorder');
      });

    if (!context.hasExplicitState && !context.summary) return;

    getLegacyPreorderElements(descriptionBlock).forEach((element) => {
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
      element.classList.add('pg-pdp-description-duplicate-preorder');
    });
  };

  const insertPreorderNote = (detailsContent, context) => {
    if (detailsContent.querySelector(':scope > .pg-pdp-preorder-note')) return;
    if (!context.summary || !context.label) return;

    const note = document.createElement('div');
    note.className = 'pg-pdp-preorder-note';
    note.setAttribute('role', 'note');

    const icon = document.createElement('span');
    icon.className = 'pg-pdp-preorder-note__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '!';

    const content = document.createElement('div');
    content.className = 'pg-pdp-preorder-note__content';

    const label = document.createElement('strong');
    label.className = 'pg-pdp-preorder-note__label';
    label.textContent = context.label;

    const text = document.createElement('span');
    text.className = 'pg-pdp-preorder-note__text';
    text.textContent = context.summary;

    content.append(label, text);
    note.append(icon, content);

    const buyButtons = Array.from(detailsContent.children).find((child) =>
      child.matches('buy-buttons, buy-buttons-component') ||
      child.querySelector?.('.add-to-cart-button, form[action*="/cart/add"]')
    );

    if (buyButtons) {
      detailsContent.insertBefore(note, buyButtons);
      return;
    }

    const policyGroup = Array.from(detailsContent.children).find((child) =>
      child.querySelector?.('.pg-pdp-policy-item')
    );

    if (policyGroup) {
      detailsContent.insertBefore(note, policyGroup);
    } else {
      detailsContent.append(note);
    }
  };

  const syncPreorderPresentation = (section, detailsContent, descriptionBlock) => {
    detailsContent.querySelector(':scope > .pg-pdp-preorder-note')?.remove();
    const context = getPreorderContext(section, descriptionBlock);
    syncLegacyPreorderText(descriptionBlock, context);
    insertPreorderNote(detailsContent, context);
  };

  const relocateProductDescription = (root = document) => {
    getProductSections(root).forEach((section) => {
      const productComponent = section.querySelector(':scope > product-component');
      const detailsContent = section.querySelector('.product-details > .group-block > .group-block-content');

      if (!productComponent || !detailsContent) return;

      const descriptionBlock = findDescriptionBlock(detailsContent);
      if (!descriptionBlock) return;

      syncPreorderPresentation(section, detailsContent, descriptionBlock);

      const shell = section.querySelector(':scope > .pg-pdp-long-content-shell[data-pg-product-information]');
      const descriptionTarget = shell?.querySelector('.pg-pdp-long-content__description-target');

      if (!shell || !descriptionTarget) return;

      descriptionBlock.classList.add('pg-pdp-description-block', 'text-block--full-width');
      descriptionBlock.style.setProperty('--width', '100%');
      descriptionBlock.style.setProperty('--max-width', '100%');
      descriptionBlock.style.setProperty('--text-align', 'left');
      descriptionTarget.append(descriptionBlock);
      structureProductInformationFlow(shell, descriptionBlock);
      rebalanceProductInformationFlow(shell, descriptionBlock);
      section.classList.add('pg-pdp-description-relocated');
    });
  };

  const enhanceProductPage = (root = document) => {
    if (!document.body?.classList.contains('template-product')) return;

    const localeCopy = getLocaleCopy();
    const policyTexts = new Set([localeCopy.shipping, localeCopy.returns]);

    root.querySelectorAll?.('p, h1, h2, h3, h4, h5, h6').forEach((node) => {
      const key = replacementKeys.get(normalize(node.textContent));
      const replacement = key ? localeCopy[key] : null;

      if (
        replacement &&
        node.children.length === 0 &&
        normalize(node.textContent) !== replacement
      ) {
        node.textContent = replacement;
      }
    });

    root.querySelectorAll?.('.product-details p').forEach((paragraph) => {
      if (!policyTexts.has(normalize(paragraph.textContent))) return;

      paragraph.closest('.group-block')?.classList.add('pg-pdp-policy-item');
    });

    relocateProductDescription(root);
  };

  const observeRecommendations = () => {
    document.querySelectorAll('product-recommendations').forEach((element) => {
      if (element.dataset.pgProductPageObserved === 'true') return;

      element.dataset.pgProductPageObserved = 'true';

      const observer = new MutationObserver(() => {
        enhanceProductPage(element);
      });

      observer.observe(element, {
        childList: true,
        subtree: true,
      });

      enhanceProductPage(element);
    });
  };

  const initialize = () => {
    enhanceProductPage();
    observeRecommendations();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    enhanceProductPage(event.target);
    observeRecommendations();
  });

  document.addEventListener('pg:preorder-context-updated', (event) => {
    const section = event.target instanceof Element ? event.target.closest('.product-information') : null;
    if (!section) return;

    requestAnimationFrame(() => {
      const detailsContent = section.querySelector('.product-details > .group-block > .group-block-content');
      const descriptionBlock =
        section.querySelector('.pg-pdp-description-block') || findDescriptionBlock(detailsContent);
      if (detailsContent && descriptionBlock) {
        syncPreorderPresentation(section, detailsContent, descriptionBlock);
      }
    });
  });
})();
